import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { useHashTabs } from "../lib/useHashTabs";
import AdminLeads from "../components/admin/AdminLeads";
import AdminSiteContent from "../components/admin/AdminSiteContent";
import AdminTestimonials from "../components/admin/AdminTestimonials";
import AdminFaq from "../components/admin/AdminFaq";
import AdminUsersPanel from "../components/admin/AdminUsersPanel";
import AdminProfile from "../components/admin/AdminProfile";
import { LogOut, ExternalLink, Settings, LayoutDashboard, Globe, Eye, User, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { logger } from "../lib/logger";
import { BACKOFFICE_TABS as allTabs } from "../lib/tabs";

export default function BackofficeDashboard() {
  const { user, profile, logout, loading, isAdmin, isDev } = useAuth();
  const navigate = useNavigate();
  
  const [tab, setTab, subTab, setSubTab, sub2Tab, setSub2Tab] = useHashTabs("leads");
  const [formSubTab, setFormSubTab] = useState("labels");
  const [allowedTabs, setAllowedTabs] = useState([]);
  const [permsLoading, setPermsLoading] = useState(true);
  const [tabModes, setTabModes] = useState({});

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.uid) return;
    
    async function loadPerms() {
      setPermsLoading(true);
      try {
        if (isDev) {
          // Developers get absolute everything in edit mode
          setAllowedTabs(allTabs);
          const modes = {};
          allTabs.forEach(t => { modes[t.uid] = "edit"; });
          
          // Force sub-tab modes too
          modes.dev_users = "edit";
          modes.dev_menu = "edit";
          modes.dev_roles = "edit";
          modes.dev_settings = "edit";
          
          setTabModes(modes);
        } else {
          const raw = user.menu_permissions ? JSON.parse(user.menu_permissions) : {};
          const modes = {};
          
          if (Array.isArray(raw)) {
            raw.forEach(t => { modes[t] = "edit"; });
          } else {
            Object.entries(raw).forEach(([k, v]) => { if (v !== "off") modes[k] = v; });
          }

          // Force 'site' and 'dev' for Admins if they have any access
          if (isAdmin) {
            modes.site = modes.site || "edit";
            modes.dev = modes.dev || "edit";
          }

          setTabModes(modes);
          const filtered = allTabs.filter(t => modes[t.uid]);
          setAllowedTabs(filtered.length > 0 ? filtered : allTabs.slice(0, 1));
        }
      } catch (err) {
        logger.error("[Backoffice] Error parsing perms:", err);
        setAllowedTabs(isDev || isAdmin ? allTabs : []);
      } finally {
        setPermsLoading(false);
      }
    }
    loadPerms();
  }, [user?.uid, isAdmin, isDev, user?.menu_permissions]);

  useEffect(() => {
    if (tab === "profile") return;
    if (allowedTabs.length > 0 && !allowedTabs.find(t => t.uid === tab)) {
      setTab(allowedTabs[0].uid);
    }
  }, [allowedTabs, tab]);

  if (loading || !user) return null;

  function onLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div data-id="page-backoffice" className="min-h-screen bg-offwhite">
      <header className="bg-white border-b border-fendi sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-10 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 outline-none hover:opacity-80 transition-opacity" data-id="backoffice-user-menu" data-testid="backoffice-user-menu">
                  <Avatar className="h-7 w-7 md:h-8 md:w-8 border border-fendi/20">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <AvatarFallback className="bg-champagne/10 text-softblack text-[9px] md:text-[10px] font-serif uppercase">
                        {profile?.display_name?.charAt(0) || user?.email?.charAt(0) || 'A'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 mt-1">
                <DropdownMenuLabel className="font-serif font-normal text-warmgray text-xs uppercase tracking-wider">
                  {profile?.display_name || user?.email}
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setTab("profile")} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4 text-warmgray" />
                  <span className="text-xs uppercase tracking-widest">Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span className="text-xs uppercase tracking-widest">Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/" className="font-serif text-lg md:text-2xl text-softblack" data-id="backoffice-logo" data-testid="backoffice-logo">
              {isDev ? "Dev" : "Hub"}<span className="text-champagne">.</span>
            </Link>
            
            <nav className="hidden lg:flex gap-2 ml-4" data-id="backoffice-tabs" data-testid="backoffice-tabs">
              {allowedTabs.map((t) =>
                t.children ? (
                  <div key={t.uid} className="relative group">
                    <button
                      onClick={() => { setTab(t.uid); setSubTab(t.children[0].uid); }}
                      data-id={`backoffice-tab-${t.uid}`} data-testid={`backoffice-tab-${t.uid}`}
                      className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === t.uid ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
                    >
                      <t.Icon size={14} strokeWidth={1.5} />
                      {t.label}
                      <ChevronDown size={12} strokeWidth={2} className="ml-1" />
                    </button>
                    <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="bg-white border border-fendi/30 rounded-lg shadow-xl py-2 min-w-[160px]">
                        {t.children.map((c) => (
                          <button
                            key={c.uid}
                            onClick={() => { setTab(t.uid); setSubTab(c.uid); }}
                            className={`block w-full text-left px-4 py-2 text-[11px] tracking-wider transition-colors ${tab === t.uid && subTab === c.uid ? "text-champagne bg-champagne/5 font-bold" : "text-warmgray hover:text-softblack hover:bg-fendi/10"}`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={t.uid}
                    onClick={() => { setTab(t.uid); setSubTab(null); }}
                    data-id={`backoffice-tab-${t.uid}`} data-testid={`backoffice-tab-${t.uid}`}
                    className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors ${tab === t.uid ? "text-softblack border-b border-champagne" : "text-warmgray hover:text-softblack"}`}
                  >
                    <t.Icon size={14} strokeWidth={1.5} />
                    {t.label}
                  </button>
                )
              )}
            </nav>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/" target="_blank" rel="noopener noreferrer" className="text-[10px] lg:text-xs text-warmgray hover:text-softblack" data-id="backoffice-view-site" data-testid="backoffice-view-site">
              <ExternalLink size={10} className="lg:hidden" />
              <span className="hidden lg:inline-flex items-center gap-1 eyebrow"><ExternalLink size={12} /> Ver site</span>
            </Link>
          </div>
        </div>
        <nav className="lg:hidden border-t border-fendi/60 flex w-full overflow-x-auto">
          {allowedTabs.map((t) => (
            <button
              key={t.uid}
              onClick={() => { setTab(t.uid); setSubTab(t.children ? t.children[0].uid : null); }}
              data-id={`backoffice-tab-mobile-${t.uid}`} data-testid={`backoffice-tab-mobile-${t.uid}`}
              className={`flex items-center justify-center flex-1 min-w-0 py-3 text-[9px] tracking-[0.2em] uppercase ${tab === t.uid ? "text-softblack border-t-2 border-champagne" : "text-warmgray"}`}
            >
              <t.Icon size={16} strokeWidth={1.5} />
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-10 py-6 md:py-12">
        {tab === "profile" ? <AdminProfile /> : permsLoading ? null : allowedTabs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <p className="font-serif text-2xl text-softblack">Sem acesso</p>
            <p className="text-warmgray text-sm mt-2">Permissões no configuradas. Contacte o programador.</p>
          </div>
        ) : (
          <>
            {tabModes[tab] === "readonly" && (
              <div className="mb-6 bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-3">
                <Eye size={16} className="text-blue-500 shrink-0" />
                <p className="text-sm text-blue-700">Modo somente leitura. Pode visualizar mas no editar.</p>
              </div>
            )}
            {tab === "leads" && <AdminLeads readOnly={tabModes.leads === "readonly"} />}
            {tab === "site" && <AdminSiteContent readOnly={tabModes.site === "readonly"} subTabModes={tabModes} defaultSubTab={subTab} onSubTabChange={setSubTab} defaultSub2={sub2Tab} onSub2Change={setSub2Tab} />}

            {tab === "testimonials" && <AdminTestimonials readOnly={tabModes.testimonials === "readonly"} />}
            {tab === "dev" && <AdminUsersPanel readOnly={tabModes.dev === "readonly"} subTabModes={tabModes} userRole={user?.role} defaultTab={subTab} onTabChange={setSubTab} />}
          </>
        )}
      </main>
    </div>
  );
}
